/**
 * Agent Service
 *
 * Сервис для работы с AI агентом.
 * Поддерживает управление тредами, отправку сообщений и получение истории.
 */

import { httpClient } from "@/infrastructure/http/HttpClient";
import { getBaseUrl } from "@/lib/insightApi";

// ============================================
// TYPES
// ============================================

export interface AgentThread {
  id: string;
  user_id: string;
  thread_name: string;
  state: Record<string, unknown> | null;
  guide_content: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface InterviewQuestionOption {
  id: string;
  label: string;
  help?: string;
}

export interface InterviewQuestion {
  id: string;
  title: string; // Заголовок (не показывается в UI, т.к. дублирует текст сообщения)
  explanation: string; // ОБЯЗАТЕЛЬНОЕ: объяснение/комментарий к вопросу от LLM
  prompt?: string;
  options: InterviewQuestionOption[];
  allow_custom?: boolean;
  multi?: boolean;
  custom_answer_hint?: string; // Текст для кастомного ответа (генерируется LLM на языке пользователя)
}

export interface InterviewGuideSection {
  id: string;
  title: string;
}

export interface InterviewGuide {
  title: string;
  markdown: string;
  sections?: InterviewGuideSection[];
}

export interface ResearchContextBrief {
  meta?: Record<string, unknown> | null;
  decision_framing?: Record<string, unknown> | null;
  product?: Record<string, unknown> | null;
  goals?: Record<string, unknown> | null;
  participants?: Record<string, unknown> | null;
  method?: Record<string, unknown> | null;
  hypotheses?: Record<string, unknown>[] | null;
  analysis?: Record<string, unknown> | null;
  outputs?: Record<string, unknown> | null;
  glossary?: Record<string, unknown>[] | null;
  limitations?: Record<string, unknown>[] | null;
  pii_policy?: Record<string, unknown> | null;
  references?: Record<string, unknown>[] | null;
  conflicts?: Record<string, unknown>[] | null;
  quality?: Record<string, unknown> | null;
}

export interface ResearchContextDocument {
  title: string;
  markdown: string;
  brief?: ResearchContextBrief | null;
  validation?: Record<string, unknown> | null;
}

export interface InterviewConfirmationOption {
  id: string;
  label: string;
  action: "generate" | "continue" | "cancel";
  help?: string;
}

export interface InterviewConfirmation {
  id: string;
  message: string;
  options: InterviewConfirmationOption[];
}

export interface ProjectCitationSource {
  title: string;
  uri: string;
  quote: string;
  project_id?: string;
  record_id?: string;
  report_type?: string;
  report_id?: string;
  anchor?: string; // якорная ссылка подраздела (section_anchor или utterance_anchor)
  source_level?: "context_level" | "project_reports_level" | "single_reports_level";
  evidence_score?: number;
}

export interface InlineCitation {
  start_index: number; // позиция начала в тексте ответа
  end_index: number; // позиция конца в тексте ответа
  source_index: number; // индекс источника в sources[]
  confidence?: number;
  mapping_reason?: "llm_json_mapper" | "heuristic_fallback";
}

export interface ProjectFileSearchStoreStatus {
  project_id: string;
  status: "indexing" | "indexed" | "failed";
  indexed_documents_count: number;
  pending_operations_count: number;
  indexed_documents_progress_percent: number;
  last_indexed_at: string | null;
  updated_at: string | null;
  error: string | null;
}

export interface AgentDocumentScopePayload {
  exclude_report_types?: string[];
  excluded_record_ids?: string[];
  excluded_project_report_ids?: string[];
}

export interface AgentChatInteraction {
  action: "answer" | "skip" | "generate";
  question_id?: string;
  selected_option_ids?: string[];
}

export type AgentTurnStatus = "running" | "completed" | "failed" | "cancelled" | "superseded";

export interface AgentTurn {
  turn_id: string;
  thread_id: string;
  status: AgentTurnStatus;
  request_payload: {
    message?: string;
    reasoning_mode?: "fast" | "thinking";
    forced_action?: "research_context" | "interview_guide" | "documentation" | "project" | null;
    project_ids?: string[];
    document_scope?: AgentDocumentScopePayload;
    interaction?: AgentChatInteraction;
    edit_of_message_id?: string;
    confirm_workspace_actions?: boolean;
  };
  assistant_message_id: string | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export type AgentStreamEventType =
  | "text_delta"
  | "status"
  | "tool_start"
  | "tool_end"
  | "tool_error"
  | "ask_project_attempt"
  | "ask_project_finish"
  | "heartbeat"
  | "done"
  | "error";

export interface AgentStreamEvent {
  v: number;
  type: AgentStreamEventType;
  ts: string;
  data: Record<string, unknown>;
}

export type AgentChatRequestError = Error & {
  status?: number;
  detail?: string;
  code?: string;
};

const STREAM_EVENTS_LOCAL_STORAGE_KEY = "aseed:agent:stream-events";
const AGENT_SSE_INTERNAL_MARKER = ":aseed-internal-frame-v1\n";

function isStreamEventsEnabled(): boolean {
  const envDefault = process.env.NEXT_PUBLIC_AGENT_STREAM_EVENTS !== "false";
  if (typeof window === "undefined") {
    return envDefault;
  }

  try {
    const override = window.localStorage.getItem(STREAM_EVENTS_LOCAL_STORAGE_KEY);
    if (!override) {
      return envDefault;
    }
    const normalized = override.trim().toLowerCase();
    if (normalized === "false" || normalized === "0" || normalized === "off") {
      return false;
    }
    if (normalized === "true" || normalized === "1" || normalized === "on") {
      return true;
    }
  } catch {
    // Ignore localStorage access errors and use env default.
  }

  return envDefault;
}

export function isAgentStreamEventsEnabled(): boolean {
  return isStreamEventsEnabled();
}

export type WorkspaceActionPayload = {
  tool: string;
  status: "ok" | "error" | "preview";
  entity_type?: string | null;
  entity_id?: string | null;
  project_id?: string | null;
  title?: string | null;
  cost?: number | null;
  message?: string | null;
};

export type MessagePart =
  | { type: "text"; text: string }
  | { type: "question"; question: InterviewQuestion }
  | { type: "guide"; guide: InterviewGuide }
  | { type: "research_context"; brief: ResearchContextDocument }
  | { type: "confirmation"; confirmation: InterviewConfirmation }
  | { type: "documentation" }
  | {
      type: "project_citation";
      sources: ProjectCitationSource[];
      inline_citations?: InlineCitation[];
    }
  | { type: "workspace_action"; action: WorkspaceActionPayload };

export interface AgentMessage {
  id: string;
  thread_id: string;
  message_type: "user" | "assistant";
  content: string;
  parts: MessagePart[] | null;
  llm_model?: "fast" | "thinking";
  mode?: "normal" | "interview_guide" | "research_context" | "documentation";
  created_at: string;
  can_edit?: boolean;
}

export interface AgentEditPreview {
  can_edit: boolean;
  requires_confirmation: boolean;
  reason?: string | null;
  actions?: Array<{
    tool: string;
    title?: string | null;
    entity_id?: string | null;
    status?: string | null;
    cost?: number | null;
  }>;
}

// ============================================
// SERVICE
// ============================================

class AgentServiceClass {
  /**
   * Получить все треды пользователя.
   *
   * @returns Список тредов, отсортированных по дате обновления
   */
  async getThreads(): Promise<AgentThread[]> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.get(`${baseUrl}/agent/threads`);

    if (!response.ok) {
      console.error("Failed to fetch threads:", response.status);
      throw new Error("Failed to fetch threads");
    }

    return response.json();
  }

  /**
   * Создать новый тред.
   *
   * @param name - Название треда (опционально)
   * @returns Созданный тред
   */
  async createThread(name?: string): Promise<AgentThread> {
    const baseUrl = getBaseUrl();
    // Ensure name is a string or use default
    const threadName = typeof name === "string" ? name : "Thread";
    const response = await httpClient.post(`${baseUrl}/agent/threads`, {
      name: threadName,
    });

    if (!response.ok) {
      console.error("Failed to create thread:", response.status);
      throw new Error("Failed to create thread");
    }

    return response.json();
  }

  /**
   * Обновить название треда.
   *
   * @param threadId - UUID треда
   * @param name - Новое название треда
   * @returns Обновленный тред
   */
  async renameThread(threadId: string, name: string): Promise<AgentThread> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.patch(
      `${baseUrl}/agent/threads/${threadId}`,
      { name }
    );

    if (!response.ok) {
      console.error("Failed to rename thread:", response.status);
      throw new Error("Failed to rename thread");
    }

    return response.json();
  }

  /**
   * Удалить тред (soft delete).
   *
   * @param threadId - UUID треда
   */
  async deleteThread(threadId: string): Promise<void> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.delete(
      `${baseUrl}/agent/threads/${threadId}`
    );

    if (!response.ok) {
      console.error("Failed to delete thread:", response.status);
      throw new Error("Failed to delete thread");
    }
  }

  /**
   * Получить тред по ID.
   *
   * @param threadId - UUID треда
   * @returns Тред
   */
  async getThread(threadId: string): Promise<AgentThread> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.get(
      `${baseUrl}/agent/threads/${threadId}`
    );

    if (!response.ok) {
      console.error("Failed to fetch thread:", response.status);
      throw new Error("Failed to fetch thread");
    }

    return response.json();
  }

  /**
   * Получить историю сообщений треда.
   *
   * @param threadId - UUID треда
   * @returns Список сообщений
   */
  async getHistory(threadId: string): Promise<AgentMessage[]> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.get(
      `${baseUrl}/agent/threads/${threadId}/history`
    );

    if (!response.ok) {
      console.error("Failed to fetch history:", response.status);
      throw new Error("Failed to fetch history");
    }

    return response.json();
  }

  /**
   * Отправить сообщение агенту и получить streaming ответ.
   *
   * @param threadId - UUID треда
   * @param message - Текст сообщения
   * @param onChunk - Callback для обработки чанков ответа
   * @param reasoningMode - Режим reasoning (fast или thinking)
   * @param forcedAction - Принудительный выбор инструмента/агента на основе активного тега
   * @param abortSignal - Signal для отмены запроса
   */
  async streamMessage(
    threadId: string,
    message: string,
    onChunk: (chunk: string) => void,
    reasoningMode: "fast" | "thinking" = "fast",
    forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
    projectIds?: string[],
    documentScope?: AgentDocumentScopePayload,
    abortSignal?: AbortSignal,
    onEvent?: (event: AgentStreamEvent) => void,
    interaction?: AgentChatInteraction,
    turnId: string = crypto.randomUUID(),
    editOfMessageId?: string,
    confirmWorkspaceActions = false,
    endpoint = "/agent/chat"
  ): Promise<AgentTurnStatus | null> {
    const baseUrl = getBaseUrl();
    const streamEventsEnabled = isStreamEventsEnabled();
    const body: Record<string, unknown> = {
      thread_id: threadId,
      message,
      reasoning_mode: reasoningMode,
      stream_events: streamEventsEnabled,
      turn_id: turnId,
    };
    if (editOfMessageId) {
      body.edit_of_message_id = editOfMessageId;
      body.confirm_workspace_actions = confirmWorkspaceActions;
    }
    
    // Добавляем forced_action только если он установлен
    const effectiveForcedAction =
      forcedAction ?? (projectIds && projectIds.length > 0 ? "project" : null);
    if (effectiveForcedAction) {
      body.forced_action = effectiveForcedAction;
    }
    if (projectIds && projectIds.length > 0) {
      body.project_ids = projectIds;
    }
    if (documentScope) {
      body.document_scope = documentScope;
    }
    if (interaction) {
      body.interaction = interaction;
    }
    
    const response = await httpClient.post(
      `${baseUrl}${endpoint}`,
      body,
      { signal: abortSignal },
      {
        timeoutMs: 120_000,
        maxAttempts: 2,
        idempotencyKey: turnId,
        requestId: turnId,
      }
    );

    if (!response.ok) {
      // Если запрос был отменен, не выбрасываем ошибку
      if (abortSignal?.aborted) {
        return null;
      }
      const payload = await response.json().catch(() => ({}));
      const detail = typeof payload?.detail === "string" ? payload.detail : undefined;
      const error = new Error(detail || `Agent chat request failed (${response.status})`) as AgentChatRequestError;
      error.name = "AgentChatRequestError";
      error.status = response.status;
      error.detail = detail;
      error.code = detail;
      console.error("Failed to send message:", response.status, detail);
      throw error;
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    let streamMode: "unknown" | "sse" | "plain" = streamEventsEnabled ? "unknown" : "plain";

    let terminalStatus: AgentTurnStatus | null = null;
    let terminalError: string | undefined;
    const emitEvent = (event: AgentStreamEvent) => {
      onEvent?.(event);
      if (event.type === "done") {
        terminalStatus = "completed";
      } else if (event.type === "error") {
        const status = event.data?.status;
        terminalStatus = status === "cancelled" ? "cancelled" : "failed";
        if (typeof event.data?.message === "string" && event.data.message.length > 0) {
          terminalError = event.data.message;
        }
      }
      if (event.type === "text_delta") {
        const text = event.data?.text;
        if (typeof text === "string" && text.length > 0) {
          onChunk(text);
        }
        return;
      }
      if (event.type === "error") {
        if (event.data?.status === "cancelled") {
          return;
        }
        const message = event.data?.message;
        if (typeof message === "string" && message.length > 0) {
          onChunk(`Error: ${message}`);
        }
      }
    };

    const flushSseFrames = () => {
      if (streamMode !== "sse") {
        return;
      }
      let separatorIndex = sseBuffer.indexOf("\n\n");
      while (separatorIndex !== -1) {
        const rawFrame = sseBuffer.slice(0, separatorIndex);
        sseBuffer = sseBuffer.slice(separatorIndex + 2);
        const frame = rawFrame.replace(/\r/g, "");
        if (!frame.trim()) {
          separatorIndex = sseBuffer.indexOf("\n\n");
          continue;
        }

        const lines = frame.split("\n");
        let eventName = "message";
        const dataLines: string[] = [];
        for (const line of lines) {
          if (line.startsWith(":")) continue;
          if (line.startsWith("event:")) {
            eventName = line.slice("event:".length).trim();
            continue;
          }
          if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).trimStart());
            continue;
          }
        }

        const dataPayload = dataLines.join("\n");
        if (eventName === "aseed" && dataPayload) {
          try {
            const parsed = JSON.parse(dataPayload) as AgentStreamEvent;
            if (parsed && typeof parsed.type === "string") {
              emitEvent(parsed);
            }
          } catch (error) {
            console.warn("Failed to parse Agent SSE frame:", error);
          }
        } else if (dataPayload) {
          onChunk(`${dataPayload}\n`);
        } else {
          onChunk(`${frame}\n`);
        }

        separatorIndex = sseBuffer.indexOf("\n\n");
      }
    };

    const resolveStreamMode = () => {
      if (streamMode !== "unknown") {
        return;
      }

      if (sseBuffer.length === 0) {
        return;
      }

      const isPotentialMarkerPrefix =
        AGENT_SSE_INTERNAL_MARKER.startsWith(sseBuffer) ||
        sseBuffer.startsWith(AGENT_SSE_INTERNAL_MARKER);
      if (!isPotentialMarkerPrefix) {
        streamMode = "plain";
        onChunk(sseBuffer);
        sseBuffer = "";
        return;
      }

      const firstNewlineIndex = sseBuffer.indexOf("\n");
      if (firstNewlineIndex === -1) {
        return;
      }

      const firstLine = sseBuffer.slice(0, firstNewlineIndex).replace(/\r$/, "");
      if (firstLine === AGENT_SSE_INTERNAL_MARKER.trim()) {
        streamMode = "sse";
        return;
      }

      streamMode = "plain";
      if (sseBuffer.length > 0) {
        onChunk(sseBuffer);
        sseBuffer = "";
      }
    };

    try {
      while (true) {
        // Проверяем отмену перед чтением
        if (abortSignal?.aborted) {
          reader.cancel();
          return null;
        }

        const { done, value } = await reader.read();

        if (done) break;

        // Проверяем отмену после чтения
        if (abortSignal?.aborted) {
          reader.cancel();
          return null;
        }

        const chunk = decoder.decode(value, { stream: true });
        if (streamEventsEnabled) {
          if (streamMode === "plain") {
            onChunk(chunk);
            continue;
          }

          sseBuffer += chunk;
          resolveStreamMode();
          const resolvedMode = streamMode as string;
          if (resolvedMode === "sse") {
            flushSseFrames();
          } else if (resolvedMode === "plain" && sseBuffer.length > 0) {
            onChunk(sseBuffer);
            sseBuffer = "";
          }
          continue;
        }
        onChunk(chunk);
      }

      if (streamEventsEnabled && sseBuffer.length > 0) {
        if ((streamMode as string) === "sse") {
          sseBuffer += "\n\n";
          flushSseFrames();
        } else {
          onChunk(sseBuffer);
        }
      }
    } catch (error) {
      // Если запрос был отменен, не выбрасываем ошибку
      if (abortSignal?.aborted || (error instanceof Error && error.name === "AbortError")) {
        return null;
      }
      throw error;
    } finally {
      reader.releaseLock();
    }
    if (terminalStatus === "failed" && terminalError) {
      const error = new Error(terminalError) as AgentChatRequestError;
      error.name = "AgentChatTurnError";
      error.detail = terminalError;
      error.code = terminalError;
      throw error;
    }
    return terminalStatus;
  }

  async previewMessageEdit(
    threadId: string,
    editOfMessageId: string,
  ): Promise<AgentEditPreview> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.post(`${baseUrl}/agent/chat/edit/preview`, {
      thread_id: threadId,
      edit_of_message_id: editOfMessageId,
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const error = new Error(
        typeof detail?.detail === "string" ? detail.detail : "Failed to preview message edit",
      ) as Error & { status?: number; detail?: unknown };
      error.status = response.status;
      error.detail = detail?.detail;
      throw error;
    }
    return response.json();
  }

  async getActiveTurn(threadId?: string): Promise<AgentTurn | null> {
    const baseUrl = getBaseUrl();
    const query = threadId ? `?thread_id=${encodeURIComponent(threadId)}` : "";
    const response = await httpClient.get(`${baseUrl}/agent/chat/turns/active${query}`);
    if (!response.ok) {
      throw new Error("Failed to fetch active chat turn");
    }
    return response.json();
  }

  async getTurn(turnId: string): Promise<AgentTurn> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.get(`${baseUrl}/agent/chat/turns/${turnId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch chat turn");
    }
    return response.json();
  }

  /**
   * Отменить выполняющийся запрос чата.
   *
   * @param threadId - UUID треда
   */
  async cancelChat(threadId: string, turnId?: string): Promise<boolean> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.post(`${baseUrl}/agent/chat/cancel`, {
      thread_id: threadId,
      ...(turnId ? { turn_id: turnId } : {}),
    });

    if (!response.ok) {
      console.error("Failed to cancel chat:", response.status);
      return false;
    }
    const result = (await response.json()) as { success?: boolean };
    return result.success === true;
  }

  async ensureProjectFileSearchStore(projectId: string): Promise<ProjectFileSearchStoreStatus> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.post(
      `${baseUrl}/agent/projects/${projectId}/file-search-store/ensure`,
      {},
      {},
      { timeoutMs: 120_000, maxAttempts: 1 }
    );
    if (!response.ok) {
      console.error("Failed to ensure project File Search store:", response.status);
      throw new Error("Failed to ensure project File Search store");
    }
    return response.json();
  }

  async getProjectFileSearchStoreStatus(projectId: string): Promise<ProjectFileSearchStoreStatus> {
    const baseUrl = getBaseUrl();
    const response = await httpClient.get(
      `${baseUrl}/agent/projects/${projectId}/file-search-store/status`,
      {},
      { timeoutMs: 120_000, maxAttempts: 1 }
    );
    if (!response.ok) {
      console.error("Failed to fetch project File Search status:", response.status);
      throw new Error("Failed to fetch project File Search status");
    }
    return response.json();
  }
}

export const agentService = new AgentServiceClass();
